import { SectionEyebrow, FadeUp } from "./_shared";

const SYSTEM_IMAGES = {
  connected: "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/n4nwcclm_An_illustrated_figure_silhouette_at_the_center_of_seven_inte.png",
  metabolic: "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/vfron9x6_An_illustrated_male_silhouette_integrated_with_DNA_helixes_a.png",
  recovery: "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/j89ocpxh_bilateral_sound_waves_and_alternating_light_pulses_around_a_.png",
};

const EXAMPLES = [
  { a: "Metabolism", b: "Hormones" },
  { a: "Sleep", b: "Appetite & Cognition" },
  { a: "Stress", b: "Recovery & Digestion" },
];

export default function UnifiedModel() {
  return (
    <section
      id="unified-model"
      data-testid="unified-model-section"
      className="relative py-24 md:py-32 bg-background"
    >
      <div className="absolute inset-0 iw-grid-bg opacity-30" aria-hidden="true" />
      <div className="relative max-w-6xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <FadeUp>
              <SectionEyebrow>The Unified Medical Model</SectionEyebrow>
              <h2 className="mt-4 font-display font-bold tracking-tight text-balance leading-[1.08] text-[clamp(1.75rem,3.4vw,2.6rem)] text-primary">
                The Body Doesn't Operate in <em className="not-italic text-accent">Silos</em>.
              </h2>
              <p className="mt-6 text-lg text-foreground/75 leading-relaxed">
                Every system influences the next. CPI reads the relationships, not isolated numbers.
              </p>
            </FadeUp>

            <FadeUp delay={0.08} className="mt-7 space-y-3">
              {EXAMPLES.map(({ a, b }) => (
                <div
                  key={a}
                  className="border-b border-border pb-3 last:border-0"
                >
                  <span className="font-mono text-[0.68rem] uppercase tracking-[0.14em] font-bold text-primary whitespace-nowrap">
                    {a} <span className="text-accent">↔</span> {b}
                  </span>
                </div>
              ))}
            </FadeUp>

            <FadeUp delay={0.12} className="mt-8">
              <blockquote
                data-testid="unified-model-positioning"
                className="border-l-2 border-primary pl-5 text-base md:text-lg text-foreground/80 leading-relaxed"
              >
                Medical care diagnoses and treats. Integrated Clinical Intelligence connects
                changing signals across systems and time.
              </blockquote>
            </FadeUp>
          </div>

          <FadeUp delay={0.1} className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-2">
            <div className="relative col-span-2 sm:col-span-1 sm:row-span-2 aspect-[4/5] overflow-hidden border-2 border-primary bg-[#efe7d9]">
              <img
                src={SYSTEM_IMAGES.connected}
                alt="Connected physiological systems shown as one integrated human model"
                data-testid="unified-model-image"
                className="h-full w-full object-cover object-center"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/65 to-transparent px-5 pb-5 pt-16">
                <span className="font-mono text-[0.58rem] uppercase tracking-[0.16em] text-white">
                  One body · Connected systems
                </span>
              </div>
            </div>
            <div className="relative aspect-square overflow-hidden border border-primary/25 bg-card">
              <img
                src={SYSTEM_IMAGES.metabolic}
                alt="Metabolic, hormonal and biological age signals shown together"
                data-testid="unified-model-metabolic-image"
                className="h-full w-full object-cover object-center"
                loading="lazy"
              />
              <div data-testid="unified-model-metabolic-caption" className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/75 to-transparent p-4 pt-12 font-mono text-xs uppercase tracking-[0.1em] text-white">
                Metabolism ↔ Hormones
              </div>
            </div>
            <div className="relative aspect-square overflow-hidden border border-primary/25 bg-card">
              <img
                src={SYSTEM_IMAGES.recovery}
                alt="Brain and bilateral wave patterns representing stress and recovery"
                data-testid="unified-model-recovery-image"
                className="h-full w-full object-cover object-center"
                loading="lazy"
              />
              <div data-testid="unified-model-recovery-caption" className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/75 to-transparent p-4 pt-12 font-mono text-xs uppercase tracking-[0.1em] text-white">
                Stress ↔ Recovery
              </div>
            </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
